import { useAppContext } from '@contexts/app';
import { reportAuthError } from '@services/auth/report';
import { validateToken } from '@services/auth/validateToken';
import { useGetAdmin } from '@services/panel/admins/get';
import { useGetUser } from '@services/resources/users/old/get';
import { uuidRegEx } from '@utils/consts';
import { getUserData } from '@utils/getUserData';
import { useEffect, useState } from 'react';
import { restoreSessionId } from './helpers';

const MISSING_ID_GRACE_MS = 3000;

type Recovery = 'idle' | 'pending' | 'done' | 'failed';

export const useAccountLoad = (type: 'admin' | 'user', hasSession: boolean) => {
  const { isResolvingAuth, setData } = useAppContext();
  const adminId = getUserData('admin').id;
  const userId = getUserData('user').id;

  const admin = useGetAdmin(adminId);
  const user = useGetUser(userId);

  const query = type === 'admin' ? admin : user;
  const id = type === 'admin' ? adminId : userId;
  const hasValidId = Boolean(id && uuidRegEx.test(id));

  const idMissing = hasSession && !hasValidId && !isResolvingAuth;
  const [missingId, setMissingId] = useState(false);
  const [recovery, setRecovery] = useState<Recovery>('idle');

  useEffect(() => {
    if (!idMissing) {
      setMissingId(false);
      return;
    }
    const timeout = window.setTimeout(() => setMissingId(true), MISSING_ID_GRACE_MS);
    return () => window.clearTimeout(timeout);
  }, [idMissing]);

  useEffect(() => {
    if (!missingId || recovery !== 'idle') return;
    setRecovery('pending');
    validateToken(type)
      .then((resp) => {
        const recoveredId = resp?.data?.id;
        if (!recoveredId || !uuidRegEx.test(recoveredId)) {
          throw new Error(`validate_token returned no ${type} id`);
        }
        restoreSessionId(type, recoveredId);
        reportAuthError(
          'missingSessionIdRecovered',
          new Error(`Recovered missing ${type} id in session`),
          'warning',
        );
        setRecovery('done');
        setData((prev) => ({ ...prev }));
      })
      .catch((error: unknown) => {
        reportAuthError('missingSessionId', error);
        setRecovery('failed');
      });
  }, [missingId, recovery, type, setData]);

  const requestFailed = Boolean(query.error) && !query.isFetching;
  const recoveryFailed = missingId && (recovery === 'failed' || recovery === 'done');
  const failed = recoveryFailed || requestFailed;

  useEffect(() => {
    if (requestFailed) reportAuthError(type === 'admin' ? 'loadAdmin' : 'loadUser', query.error);
  }, [requestFailed, type, query.error]);

  return {
    failed,
    canRetry: hasValidId,
    retry: () => void query.refetch(),
  };
};
